import { Action } from 'store/store';
import { ActiveInputType } from 'portfolio/PortfolioState';
import { WidgetInfo } from 'widgets/WidgetInfo';
import { PortfolioItem } from 'portfolio/PortfolioItem';
import { PortfolioSelection } from 'portfolio/PortfolioSelection';
import { AnyAction } from 'redux';

export enum PortfolioActionType {
    PORTFOLIO_SET_ACTIVE_INPUT = 'PORTFOLIO_SET_ACTIVE_INPUT',
    PORTFOLIO_SET_SELECTION = 'PORTFOLIO_SET_SELECTION',
}

export interface PortfolioSetActiveInputPayload {
    activeInputType: ActiveInputType;
    activeInputWidget?: WidgetInfo;
}

/*
 * Dispatched asyncronously to enable consumption by middleware
 */
export type PortfolioSetActiveInputAction = Action<
    PortfolioActionType.PORTFOLIO_SET_ACTIVE_INPUT,
    PortfolioSetActiveInputPayload
>;

export const portfolioSetActiveInputAction: (
    activeInputType: ActiveInputType,
    activeInputWidget?: WidgetInfo
) => PortfolioSetActiveInputAction = (activeInputType, activeInputWidget?) => ({
    type: PortfolioActionType.PORTFOLIO_SET_ACTIVE_INPUT,
    payload: {
        activeInputType,
        activeInputWidget,
    },
});

export function actionIsPortfolioSetActiveInputAction(
    action: AnyAction
): action is PortfolioSetActiveInputAction {
    return action.type === PortfolioActionType.PORTFOLIO_SET_ACTIVE_INPUT;
}

export enum FilteredPortfolioChangedEventType {
    /*
     * The complete membership has changed - consuming widgets calculate using the full set of results (rather than the delta)
     * i.e. a filter has changed, or active input has changed
     */
    RESET = 'Reset',
    /*
     * An update to aportfolio item (or addition or removal) has caused this change. Consuming widgets may just apply the delta.
     */
    UPDATED = 'Updated',
}

export interface FilteredPortfolioChangeddEventMemberUpdate {
    prev?: PortfolioItem;
    next?: PortfolioItem;
}

export interface FilteredPortfolioChangedEventUpdates {
    added: FilteredPortfolioChangeddEventMemberUpdate[];
    removed: FilteredPortfolioChangeddEventMemberUpdate[];
    updated: FilteredPortfolioChangeddEventMemberUpdate[];
}

export interface FilteredPortfolioChangedPayload {
    changedType: FilteredPortfolioChangedEventType;
    batchUpdateInProgress?: boolean;

    /*
     * If the type is UPDATED, this has details of the updates
     */
    updates?: FilteredPortfolioChangedEventUpdates;
}

export type PortfolioSetSelectionAction = Action<
    PortfolioActionType.PORTFOLIO_SET_SELECTION,
    PortfolioSelection
>;

export function portfolioSetSelection(selection: PortfolioSelection) {
    return {
        type: PortfolioActionType.PORTFOLIO_SET_SELECTION,
        payload: selection,
    };
}

export function isPortfolioSetSelectionAction(
    action: AnyAction
): action is PortfolioSetSelectionAction {
    return action.type === PortfolioActionType.PORTFOLIO_SET_SELECTION;
}

export type PortfolioAction =
    | PortfolioSetActiveInputAction
    | PortfolioSetSelectionAction;
